import Modal from '@components/Modal'
import React, { useState } from 'react'
import { FormFieldStack, FormGroup, FormInput, FormLabel } from '@pages/Itinerary/AddEventModal/FormComponents.styles'
import { Button, ButtonGroup } from '@pages/Itinerary/AddEventModal/FormComponents.styles'
import { TestIds } from '@/testIds'
import { PROFILE_NAME_MAX_LENGTH, profileNamePatch } from '@lib/profileName'

interface EditNameModalProps {
	isOpen: boolean
	onClose: () => void
	onSave: (firstName: string, lastName: string) => void
	initialFirstName: string
	initialLastName: string
	isUpdating?: boolean
}

const EditNameModal: React.FC<EditNameModalProps> = ({
	isOpen,
	onClose,
	onSave,
	initialFirstName,
	initialLastName,
	isUpdating = false,
}) => {
	const [firstName, setFirstName] = useState(initialFirstName)
	const [lastName, setLastName] = useState(initialLastName)

	React.useEffect(() => {
		if (isOpen) {
			setFirstName(initialFirstName)
			setLastName(initialLastName)
		}
	}, [isOpen, initialFirstName, initialLastName])

	// The profile-name step's rule: both names, trimmed and capped. Saving a blank
	// one here would make the account nameless again, which the step then asks for.
	const patch = profileNamePatch(firstName, lastName)

	const handleSave = () => {
		if (patch) onSave(patch.firstName, patch.lastName)
	}

	const hasChanges = firstName.trim() !== initialFirstName.trim() || lastName.trim() !== initialLastName.trim()

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title="Edit Name"
			showCloseButton={true}
			size="medium"
			footer={
				<ButtonGroup $align="right">
					<Button variant="secondary" onClick={onClose} disabled={isUpdating}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleSave}
						disabled={!patch || !hasChanges || isUpdating}
						data-testid={TestIds.me.saveButton}
					>
						{isUpdating ? 'Saving...' : 'Save Changes'}
					</Button>
				</ButtonGroup>
			}
		>
			<FormFieldStack>
				<FormGroup>
					<FormLabel htmlFor="first-name">First Name</FormLabel>
					<FormInput
						id="first-name"
						data-testid={TestIds.me.firstNameField}
						type="text"
						value={firstName}
						maxLength={PROFILE_NAME_MAX_LENGTH}
						onChange={e => setFirstName(e.target.value)}
						placeholder="Enter first name"
						autoFocus
					/>
				</FormGroup>

				<FormGroup>
					<FormLabel htmlFor="last-name">Last Name</FormLabel>
					<FormInput
						id="last-name"
						data-testid={TestIds.me.lastNameField}
						type="text"
						value={lastName}
						maxLength={PROFILE_NAME_MAX_LENGTH}
						onChange={e => setLastName(e.target.value)}
						placeholder="Enter last name"
					/>
				</FormGroup>
			</FormFieldStack>
		</Modal>
	)
}

export default EditNameModal

